<template>
  <v-container class="users-page" fluid>
    <div class="users-header">
      <h1 class="users-header__title">{{ $route.meta.title }}</h1>
      <div class="users-header__actions">
        <v-btn
          color="primary"
          class="users-header__btn text-none"
          dark
          rounded
          to="/user/new"
        >
          <v-icon left>mdi-account-plus-outline</v-icon>
          Новый пользователь
        </v-btn>
      </div>
    </div>

    <v-card class="users-block mt-3" flat outlined>
      <div class="users-filters">
        <div class="users-filters__title">
          <v-icon left small>mdi-filter-variant</v-icon>
          Фильтры
        </div>
        <div class="users-filters__row">
          <v-autocomplete
            v-model="filterRoleId"
            :items="typesRole"
            item-text="name"
            item-value="value"
            placeholder="Тип роли"
            prepend-inner-icon="mdi-account-badge-outline"
            class="users-filters__field"
            outlined
            dense
            hide-details
            clearable
          />
          <v-switch
            v-model="showBlockedUsers"
            class="users-filters__switch"
            color="primary"
            hide-details
            dense
            label="Показать заблокированных пользователей"
          />
        </div>
      </div>
      <v-divider class="my-0" />
      <v-data-table
        class="table"
        :headers="headers"
        :items="getUsers"
        :items-per-page="15"
        :footer-props="{
          'items-per-page-options': [10, 15, 20, 50, 100],
          showFirstLastPage: true,
        }"
      >
        <template v-slot:item.name="{ item }">
          <span>{{ item.name }}</span>
          <template v-if="item.test_phone_activate">
            <v-tooltip bottom>
              <template v-slot:activator="{ on, attrs }">
                <v-btn
                  icon
                  text
                  small
                  class="btn__icon pa-0 ml-2"
                  width="18"
                  height="18"
                  v-bind="attrs"
                  v-on="on"
                  @contextmenu.prevent="copyToClipboard(item.test_phone_activate)"
                >
                  <v-icon size="small">mdi-phone</v-icon>
                </v-btn>
              </template>
              <span>{{ formatPhoneNumber(item.test_phone_activate) }}</span>
            </v-tooltip>
          </template>
        </template>
        <template v-slot:item.blocked="{ item }">
          <v-chip
            :color="item.blocked ? 'red lighten-2' : 'green lighten-2'"
            :text-color="item.blocked ? 'red darken-4' : 'green darken-4'"
            outlined
            small
            class="cursor-pointer"
            @click="openBlockDialog(item)"
          >
            {{ item.blocked ? 'Заблокирован' : 'Активен' }}
          </v-chip>
        </template>
        <template v-slot:item.agent="{ item }">
          {{ getAgentName(item.agent_id) }}
        </template>
        <template v-slot:item.role="{ item }">
          {{ getRole(item.role.id) }}
        </template>
        <template v-slot:item.createdAt="{ item }">
          {{ ruDateFormat(item.created_at) }}
        </template>
        <template v-slot:item.residential_complexes="{ item }">
          <div v-if="item.residential_complexes && item.residential_complexes.length">
            <div v-for="rc in item.residential_complexes" :key="rc.id">
              <router-link class="routerLink" :to="`/residential-complexes/${rc.id}/view`">
                {{ rc.name }}
              </router-link>
            </div>
          </div>
        </template>
        <template v-slot:item.actions="{ item }">
          <v-row justify="center" class="flex-nowrap">
            <v-tooltip bottom>
              <template v-slot:activator="{ on, attrs }">
                <v-btn
                  icon
                  text
                  small
                  v-bind="attrs"
                  v-on="on"
                  class="btn__icon pa-0 mr-1"
                  width="18"
                  height="18"
                  :to="`/user/${item.id}/edit`"
                >
                  <v-icon size="large">mdi-pencil-outline</v-icon>
                </v-btn>
              </template>
              <span>Редактировать</span>
            </v-tooltip>
            <v-tooltip bottom>
              <template v-slot:activator="{ on, attrs }">
                <v-btn
                  icon
                  text
                  small
                  v-bind="attrs"
                  v-on="on"
                  class="btn__icon pa-0"
                  width="18"
                  height="18"
                  @click.stop="$set(dialog, item.id, true)"
                >
                  <v-icon size="large">mdi-close</v-icon>
                </v-btn>
              </template>
              <span>Удалить</span>
            </v-tooltip>
            <v-dialog
              v-model="dialog[item.id]"
              persistent
              :retain-focus="false"
              max-width="400"
              content-class="dialog-border-radius"
            >
              <BaseDeleteModal
                :active.sync="dialog[item.id]"
                @close="$set(dialog, item.id, false)"
                @submit="removeUser(item.id)"
                messageTitle="Удалить пользователя?"
                messageText="Подтверждая, вы удалите пользователя."
                confirm-text="Удалить"
                confirm-color="error"
              />
            </v-dialog>
          </v-row>
        </template>
      </v-data-table>
    </v-card>

    <v-dialog v-model="blockDialog" max-width="450" content-class="dialog-border-radius" persistent>
      <v-card class="users-block-dialog">
        <v-card-title class="users-block-dialog__title">
          {{ selectedUser && selectedUser.blocked ? 'Разблокировать пользователя?' : 'Заблокировать пользователя?' }}
        </v-card-title>
        <v-card-text class="users-block-dialog__text">
          Вы уверены, что хотите
          {{ selectedUser && selectedUser.blocked ? 'разблокировать' : 'заблокировать' }}
          пользователя <strong>{{ selectedUser && selectedUser.name }}</strong>?
        </v-card-text>
        <v-card-actions class="users-block-dialog__actions">
          <v-spacer />
          <v-btn text class="text-none" @click="blockDialog = false">Отмена</v-btn>
          <v-btn
            :color="selectedUser && selectedUser.blocked ? 'success' : 'error'"
            class="text-none"
            depressed
            rounded
            @click="toggleUserBlock"
          >
            {{ selectedUser && selectedUser.blocked ? 'Разблокировать' : 'Заблокировать' }}
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </v-container>
</template>

<script>
import { mapGetters, mapActions } from 'vuex';
import ruDateFormatMixin from '@/mixins/ruDateFormat';
import BaseDeleteModal from '@/components/BaseDeleteModal.vue';
import formatPhoneNumber from '@/mixins/formatPhoneNumber';

export default {
  name: 'Users',
  components: { BaseDeleteModal },
  mixins: [ruDateFormatMixin, formatPhoneNumber],
  data () {
    return {
      dialog: {},
      filterRoleId: 0,
      filterName: '',
      blockDialog: false,
      selectedUser: null,
      showBlockedUsers: false,
      resResidentialComplexes: [],
      typesRole: [
        { name: 'Администратор', value: 1 },
        { name: 'Админ-инженер', value: 5 },
        { name: 'Менеджер', value: 6 },
        { name: 'Аналитик', value: 7 },
        { name: 'Кол-центр', value: 8 },
        { name: 'Мастер', value: 10 },
        { name: 'Администратор КПП', value: 11 },
      ],
      headers: [
        { text: 'id', value: 'id' },
        { text: 'Имя', value: 'name', sortable: false },
        { text: 'Роль', value: 'role', sortable: false },
        { text: 'Email', value: 'email', sortable: false },
        { text: 'Статус', value: 'blocked', width: '8%', sortable: false },
        { text: 'Агент', value: 'agent', sortable: false },
        { text: 'Жилой комплекс', value: 'residential_complexes', sortable: false },
        { text: 'Дата и время создания', value: 'createdAt', sortable: false },
        { text: '', value: 'actions', sortable: false },
      ],
    };
  },
  computed: {
    ...mapGetters({
      users: 'User/users',
      agents: 'Agents/agents',
    }),
    typesRoleTable () {
      return this.typesRole.reduce((acc, role) => {
        acc[role.value] = role.name;
        return acc;
      }, {});
    },
    getUsers () {
      let data = this.users;

      if (this.filterRoleId) {
        data = data.filter(user => {
          return user.role_id.toString().toLowerCase().includes(this.filterRoleId);
        });
      }

      if (this.showBlockedUsers === false) {
        data = data.filter(user => user.blocked === false);
      }

      return data;
    },
  },
  methods: {
    copyToClipboard (telephone) {
      try {
        const text = String(telephone);
        navigator.clipboard.writeText(text);
        this.$flash_success('Телефон скопирован!');
      } catch (e) {
        this.$flash_warning('Не удалось скопировать номер телефона');
      }
    },
    ...mapActions({
      loadUsers: 'User/loadUsers',
      deleteUser: 'User/deleteUser',
      toggleBlockUser: 'User/toggleBlockUser',
      getResidentialComplex: 'ResidentComplex/getComplex',
    }),
    getAgentName (agentId) {
      const agent = this.agents.find(agent => agent.id === agentId);
      if (agent) {
        return agent.user.name;
      }
      return '';
    },
    openBlockDialog (user) {
      this.selectedUser = { ...user };
      this.blockDialog = true;
    },
    toggleUserBlock () {
      if (!this.selectedUser) return;

      const newBlockedStatus = !this.selectedUser.blocked;
      this.blockDialog = false;
      this.toggleBlockUser({
        id: this.selectedUser.id,
        blocked: newBlockedStatus,
      })
        .then(() => {
          this.getData();
          this.$flash_success('Изменения сохранены, Статус пользователя обновлён.');
        })
        .catch((err) => {
          console.error(err);
          this.$flash_warning('Ошибка при обновлении статуса');
        });
    },
    getRole (id) {
      return this.typesRoleTable[id];
    },
    removeUser (id) {
      this.deleteUser(id)
        .then(() => {
          this.getData();
        });
      this.dialog[id] = false;
    },
    async getData () {
      const filters = {};
      if (this.filterRoleId) filters.role_id = this.filterRoleId;
      if (this.filterName) filters.name = this.filterName;

      try {
        await this.loadUsers({ filters });
      } catch (e) {
        console.error(e);
      }
    },
  },
  async created () {
    await this.getData();
    const data = await this.getResidentialComplex();
    this.resResidentialComplexes = data.data;
  },
};
</script>

<style lang="scss" scoped>
.users-page {
  padding: 24px;
}

.users-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  flex-wrap: wrap;
  gap: 12px;
  padding: 8px 0;
}

.users-header__title {
  font-size: 1.5rem;
  font-weight: 400;
  color: #37474f;
  margin: 0;
  letter-spacing: -0.02em;
}

.users-header__actions {
  display: flex;
  align-items: center;
  gap: 10px;
}

.users-header__btn {
  text-transform: none;
  font-weight: 500;
}

.users-block {
  border-radius: 12px;
  overflow: hidden;
}

.users-filters {
  padding: 16px 20px 12px;
}

.users-filters__title {
  font-size: 0.875rem;
  font-weight: 600;
  color: #546e7a;
  padding: 0 0 10px 0;
  margin: 0;
  display: flex;
  align-items: center;
}

.users-filters__row {
  display: flex;
  align-items: center;
  gap: 16px;
  flex-wrap: wrap;
}

.users-filters__field {
  max-width: 280px;
  width: 100%;
  min-width: 180px;
}

.users-filters__switch {
  margin: 0;
}

.users-block-dialog {
  border-radius: 12px;
  overflow: hidden;
}

.users-block-dialog__title {
  font-size: 1.125rem;
  font-weight: 600;
  color: #37474f;
  padding: 20px 24px 8px;
}

.users-block-dialog__text {
  font-size: 0.9375rem;
  color: #546e7a;
  padding: 0 24px 16px;
  line-height: 1.5;
}

.users-block-dialog__actions {
  padding: 0 24px 20px;
}

.routerLink {
  text-decoration: none;
}

.v-application a {
  color: #434850;
}

a:hover.routerLink {
  text-decoration: underline;
}

@media screen and (max-width: 600px) {
  .users-header {
    flex-direction: column;
    align-items: flex-start;
  }

  .users-header__title {
    font-size: 1.4rem;
  }

  .users-filters__field {
    max-width: none;
  }
}
</style>
